import axios from 'axios';

/**
 * 3CX Call Control API Service
 * Documentation: https://www.3cx.com/docs/call-control-api/
 */

class ThreeCXService {
  constructor() {
    // These will be loaded from environment variables
    this.apiUrl = process.env.REACT_APP_3CX_API_URL || '';
    this.clientId = process.env.REACT_APP_3CX_CLIENT_ID || '';
    this.clientSecret = process.env.REACT_APP_3CX_CLIENT_SECRET || '';
    this.extensionNumber = process.env.REACT_APP_3CX_EXTENSION || '';
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Authenticate with 3CX API and get access token
   */
  async authenticate() {
    try {
      // Check if we already have a valid token
      if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
        return this.accessToken;
      }

      const response = await axios.post(
        `${this.apiUrl}/connect/token`,
        new URLSearchParams({
          grant_type: 'client_credentials',
          client_id: this.clientId,
          client_secret: this.clientSecret,
          scope: 'call_control'
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      this.accessToken = response.data.access_token;
      // Tokens are valid for 60 minutes, refresh 5 minutes before expiry
      this.tokenExpiry = Date.now() + ((response.data.expires_in - 300) * 1000);

      return this.accessToken;
    } catch (error) {
      console.error('3CX authentication error:', error);
      throw new Error(`Failed to authenticate with 3CX: ${error.message}`);
    }
  }

  /**
   * Get authorization headers for API requests
   */
  async getAuthHeaders() {
    const token = await this.authenticate();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Initiate an outbound call
   * @param {string} phoneNumber - The phone number to call (E.164 format recommended)
   * @param {string} callerId - Optional caller ID to display
   * @returns {Promise<Object>} Call details including call ID
   */
  async makeCall(phoneNumber, callerId = null) {
    try {
      const headers = await this.getAuthHeaders();

      // Format phone number - ensure it has country code
      let formattedNumber = phoneNumber;
      if (!formattedNumber.startsWith('+')) {
        // Assuming Zambia (+260) if no country code
        formattedNumber = `+260${formattedNumber.replace(/^0+/, '')}`;
      }

      const payload = {
        extension: this.extensionNumber,
        destination: formattedNumber,
        ...(callerId && { callerId })
      };

      console.log('3CX: Initiating call to', formattedNumber);

      const response = await axios.post(
        `${this.apiUrl}/api/call-control/v1/calls`,
        payload,
        { headers }
      );

      console.log('3CX: Call initiated successfully', response.data);

      return {
        success: true,
        callId: response.data.callId,
        status: response.data.status,
        data: response.data
      };
    } catch (error) {
      console.error('3CX call initiation error:', error.response?.data || error);
      throw new Error(
        `Failed to initiate call: ${error.response?.data?.message || error.message}`
      );
    }
  }

  /**
   * End an active call
   * @param {string} callId - The ID of the call to end
   * @returns {Promise<Object>} Result of the operation
   */
  async endCall(callId) {
    try {
      const headers = await this.getAuthHeaders();

      console.log('3CX: Ending call', callId);

      const response = await axios.delete(
        `${this.apiUrl}/api/call-control/v1/calls/${callId}`,
        { headers }
      );

      console.log('3CX: Call ended successfully');

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('3CX call end error:', error.response?.data || error);
      throw new Error(
        `Failed to end call: ${error.response?.data?.message || error.message}`
      );
    }
  }

  /**
   * Get the status of a specific call
   * @param {string} callId - The ID of the call
   * @returns {Promise<Object>} Call status details
   */
  async getCallStatus(callId) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.get(
        `${this.apiUrl}/api/call-control/v1/calls/${callId}`,
        { headers }
      );

      return {
        success: true,
        status: response.data.status,
        duration: response.data.duration,
        data: response.data
      };
    } catch (error) {
      console.error('3CX call status error:', error.response?.data || error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Transfer an active call to another extension or number
   * @param {string} callId - The ID of the call to transfer
   * @param {string} destination - Extension or phone number to transfer to
   * @returns {Promise<Object>} Result of the operation
   */
  async transferCall(callId, destination) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.post(
        `${this.apiUrl}/api/call-control/v1/calls/${callId}/transfer`,
        { destination },
        { headers }
      );

      console.log('3CX: Call transferred successfully');

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('3CX call transfer error:', error.response?.data || error);
      throw new Error(
        `Failed to transfer call: ${error.response?.data?.message || error.message}`
      );
    }
  }

  /**
   * Put a call on hold
   * @param {string} callId - The ID of the call
   * @returns {Promise<Object>} Result of the operation
   */
  async holdCall(callId) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.post(
        `${this.apiUrl}/api/call-control/v1/calls/${callId}/hold`,
        {},
        { headers }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('3CX call hold error:', error.response?.data || error);
      throw new Error(`Failed to hold call: ${error.message}`);
    }
  }

  /**
   * Resume a call from hold
   * @param {string} callId - The ID of the call
   * @returns {Promise<Object>} Result of the operation
   */
  async resumeCall(callId) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.post(
        `${this.apiUrl}/api/call-control/v1/calls/${callId}/resume`,
        {},
        { headers }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('3CX call resume error:', error.response?.data || error);
      throw new Error(`Failed to resume call: ${error.message}`);
    }
  }

  /**
   * Get all active calls for the extension
   * @returns {Promise<Array>} List of active calls
   */
  async getActiveCalls() {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.get(
        `${this.apiUrl}/api/call-control/v1/extensions/${this.extensionNumber}/calls`,
        { headers }
      );

      return {
        success: true,
        calls: response.data,
        count: response.data.length
      };
    } catch (error) {
      console.error('3CX get active calls error:', error.response?.data || error);
      return {
        success: false,
        calls: [],
        count: 0,
        error: error.message
      };
    }
  }

  /**
   * Check if 3CX is properly configured
   * @returns {boolean} True if all required config is present
   */
  isConfigured() {
    return !!(this.apiUrl && this.clientId && this.clientSecret && this.extensionNumber);
  }
}

// Export a singleton instance
const threeCXService = new ThreeCXService();
export default threeCXService;
